/**
 * MyWardsCard
 *
 * Muestra las "personas a cargo" (wards) del usuario actual:
 * personas cuyo `guardianUserId == currentUser.uid`.
 *
 * Permite editar campos no críticos (contacto, salud, foto) mediante
 * `updateWardSafe`. Las firestore.rules garantizan que campos sensibles
 * (nombre, documento, fecha de nacimiento, vínculos) NO se puedan modificar.
 */

import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  Input,
  Modal,
  ModalFooter,
} from "@/components/ui";
import { Users, AlertCircle, Pencil, UserPlus } from "lucide-react";
import { subscribeToWardsOf, updateWardSafe } from "@/lib/services/persons";
import type { Person } from "@/types";
import { useAuthStore } from "@/stores/authStore";
import RegisterWardModal from "./RegisterWardModal";
import DocumentsCard from "./DocumentsCard";

export default function MyWardsCard({
  hideWhenEmpty = false,
}: {
  /** Si true, no renderiza nada cuando el usuario no tiene dependientes
   *  (útil para el dashboard, donde no queremos mostrar la tarjeta vacía). */
  hideWhenEmpty?: boolean;
} = {}) {
  const { user } = useAuthStore();
  const [wards, setWards] = useState<Person[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Person | null>(null);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeToWardsOf(
      user.uid,
      (list) => {
        setWards(list);
        setError(null);
      },
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [user?.uid]);

  if (!user) return null;
  // En el dashboard: ocultar por completo si aún carga o no hay dependientes.
  if (hideWhenEmpty && (wards === null || wards.length === 0) && !error)
    return null;

  return (
    <>
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
              <Users className="w-4 h-4 text-white" />
            </div>
            Personas a mi cargo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <button
            onClick={() => setRegistering(true)}
            className="mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            <UserPlus className="w-4 h-4" /> Registrar a una persona a mi cargo
          </button>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
            </div>
          )}

          {!error && wards === null && (
            <p className="text-sm text-gray-500">Cargando…</p>
          )}

          {!error && wards && wards.length === 0 && (
            <div className="text-sm text-gray-600">
              <p>No tienes personas registradas a tu cargo.</p>
              <p className="text-xs text-gray-500 mt-1">
                Cuando un menor sea inscrito desde tu cuenta, aparecerá aquí y
                recibirás sus notificaciones en tu correo.
              </p>
            </div>
          )}

          {!error && wards && wards.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {wards.map((w) => (
                <li
                  key={w.id}
                  className="py-3 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {w.photoUrl ? (
                      <img
                        src={w.photoUrl}
                        alt={w.fullName}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-sm font-medium">
                        {(w.firstName?.[0] ?? "").toUpperCase()}
                        {(w.lastName?.[0] ?? "").toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {w.fullName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {w.idDocumentType} {w.idDocumentNumber}
                        {w.guardianRelation && (
                          <> · Relación: {w.guardianRelation}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">A cargo</Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(w)}
                      title="Editar datos no críticos"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditWardModal
          ward={editing}
          userId={user.uid}
          onClose={() => setEditing(null)}
        />
      )}

      <RegisterWardModal
        isOpen={registering}
        onClose={() => setRegistering(false)}
      />
    </>
  );
}

interface EditWardModalProps {
  ward: Person;
  userId: string;
  onClose: () => void;
}

function EditWardModal({ ward, userId, onClose }: EditWardModalProps) {
  const [phone, setPhone] = useState(ward.phone ?? "");
  const [address, setAddress] = useState(ward.address ?? "");
  const [healthInsurance, setHealthInsurance] = useState(
    ward.healthInsurance ?? "",
  );
  const [allergies, setAllergies] = useState(ward.allergies ?? "");
  const [medications, setMedications] = useState(ward.medications ?? "");
  const [medicalConditions, setMedicalConditions] = useState(
    ward.medicalConditions ?? "",
  );
  const [guardianRelation, setGuardianRelation] = useState<
    Person["guardianRelation"] | ""
  >(ward.guardianRelation ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await updateWardSafe(
        ward.id,
        {
          phone: phone.trim(),
          address: address.trim(),
          healthInsurance: healthInsurance.trim(),
          allergies: allergies.trim(),
          medications: medications.trim(),
          medicalConditions: medicalConditions.trim(),
          guardianRelation: guardianRelation || undefined,
        },
        userId,
      );
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Editar datos de ${ward.fullName}`}
      description="Solo puedes editar contacto y salud. Nombre, documento y fecha de nacimiento los gestiona el director del club."
      size="lg"
    >
      {err && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Relación
          </label>
          <select
            value={guardianRelation}
            onChange={(e) =>
              setGuardianRelation(
                e.target.value as Person["guardianRelation"] | "",
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">— Sin especificar —</option>
            <option value="padre">Padre</option>
            <option value="madre">Madre</option>
            <option value="tutor">Tutor</option>
            <option value="abuelo">Abuelo</option>
            <option value="abuela">Abuela</option>
            <option value="hermano">Hermano</option>
            <option value="hermana">Hermana</option>
            <option value="otro">Otro</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="3001234567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              EPS / Sisbén
            </label>
            <Input
              value={healthInsurance}
              onChange={(e) => setHealthInsurance(e.target.value)}
              placeholder="EPS o seguro"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dirección
          </label>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Calle, ciudad"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Alergias
          </label>
          <textarea
            value={allergies}
            onChange={(e) => setAllergies(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Penicilina, polen, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Medicamentos permanentes
          </label>
          <textarea
            value={medications}
            onChange={(e) => setMedications(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Inhalador, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Condiciones médicas
          </label>
          <textarea
            value={medicalConditions}
            onChange={(e) => setMedicalConditions(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Asma, diabetes, etc."
          />
        </div>

        <div className="pt-2">
          <DocumentsCard
            person={ward}
            updateFn={(patch) => updateWardSafe(ward.id, patch, userId)}
          />
        </div>
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
